import { useState, useEffect } from 'react';
import { connect } from 'react-redux';
import getContent from 'lib/getContent';

/**
 * useTrackData - Custom hook for fetching track data
 *
 * Best practice: Keep data fetching separate from context state management.
 * This hook handles the API call and loading states, while TrackProvider
 * manages the track state once loaded.
 *
 * @param {string} trackSlug - Slug of the track to fetch
 * @param {string} productSlug - Product slug (e.g., 'wlc')
 * @param {string} authToken - Authentication token from Redux
 * @param {Object} options - Additional options
 * @param {number} options.depth - How many levels of children to include (e.g., 2)
 * @param {boolean} options.enabled - Whether to fetch immediately (default: true)
 *
 * @returns {Object} { track, isLoading, error, refetch }
 *
 * @example
 * function TrackPage() {
 *   const { authToken } = useSelector(state => state.user);
 *   const { track, isLoading, error } = useTrackData(
 *     'wlc-track-healthy-eating',
 *     'wlc',
 *     authToken
 *   );
 *
 *   if (isLoading) return <Loading />;
 *   if (error) return <Error message={error} />;
 *
 *   return (
 *     <TrackProvider initialTrack={track}>
 *       <TrackContent />
 *     </TrackProvider>
 *   );
 * }
 */
export const useTrackData = (trackSlug, productSlug, authToken, options = {}) => {
  const { depth = null, enabled = true } = options;
  const query = depth != null ? `?depth=${depth}` : '';
  const [track, setTrack] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrack = async () => {
    if (!trackSlug || !authToken || !enabled) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await getContent(trackSlug, productSlug, authToken, query);
      setTrack(data);
    } catch (err) {
      console.error('Error fetching track:', err);
      setError(err.message || 'Failed to load track');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTrack();
  }, [trackSlug, productSlug, authToken, query, enabled]);

  return {
    track,
    isLoading,
    error,
    refetch: fetchTrack
  };
};

/**
 * useTrackData - Custom hook for fetching track data
 *
 * Best practice: Keep data fetching separate from context state management.
 * This hook handles the API call and loading states, while TrackProvider
 * manages the track state once loaded.
 *
 * @param {string} productSlug - Product slug (e.g., 'wlc')
 * @param {string} authToken - Authentication token from Redux
 * @param {Object} options - Additional options
 * @param {number} options.depth - How many levels of children to include (e.g., 2)
 * @param {boolean} options.enabled - Whether to fetch immediately (default: true)
 *
 * @returns {Object} { trackList, isLoading, error, refetch }
 */
export const useTrackListData = (productSlug, authToken, options = {}) => {
  const { depth = null, enabled = true } = options;
  const query = depth != null ? `?depth=${depth}` : '';
  const [trackList, setTrackList] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);

  const fetchTrackList = async () => {
    if (!productSlug || !authToken || !enabled) {
      setIsLoading(false);
      return;
    }

    try {
      setIsLoading(true);
      setError(null);
      const data = await getContent(`${productSlug}-tracks-list`, productSlug, authToken, query);
      const _trackList = data.Children.map(child => ({
        ...child.displayProps,
        slug: child.slug,
        hidden: !!child.data?.hidden
      }));
      setTrackList(_trackList);
    } catch (err) {
      console.error('Error fetching track list:', err);
      setError(err.message || 'Failed to load track list');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchTrackList();
  }, [productSlug, authToken, query, enabled]);

  return {
    trackList,
    isLoading,
    error,
    refetch: fetchTrackList
  };
};

/**
 * withTrackData - HOC that fetches track data and passes it as props
 *
 * Use this for class components that need to fetch track data.
 *
 * @param {React.Component} Component - Component to wrap
 * @param {Function} getTrackSlug - Function that extracts trackSlug from props
 * @param {Function} getProductSlug - Function that extracts productSlug from props
 * @param {Object} options - Additional options for fetching
 *
 * @example
 * class TrackPage extends Component {
 *   render() {
 *     const { track, isLoadingTrack, trackError } = this.props;
 *     // ... render logic
 *   }
 * }
 *
 * export default withTrackData(
 *   TrackPage,
 *   props => props.match.params.trackSlug,
 *   () => 'wlc',
 *   { depth: '?depth=2' }
 * );
 */
export const withTrackData = (Component, getTrackSlug, getProductSlug, options = {}) => {
  const WrappedComponent = props => {
    const trackSlug = getTrackSlug(props);
    const productSlug = getProductSlug(props);
    const { track, isLoading, error, refetch } = useTrackData(trackSlug, productSlug, props.authToken, options);

    return <Component {...props} track={track} isLoadingTrack={isLoading} trackError={error} refetchTrack={refetch} />;
  };

  WrappedComponent.displayName = `withTrackData(${Component.displayName || Component.name || 'Component'})`;

  return WrappedComponent;
};

/**
 * connectTrackData - Redux-connected HOC for fetching track data
 *
 * Combines Redux connect with track data fetching. Use this when you need
 * both Redux state and track data in a class component.
 *
 * @example
 * class TrackPage extends Component {
 *   render() {
 *     const { track, isLoadingTrack, user } = this.props;
 *     // ... render logic
 *   }
 * }
 *
 * export default connectTrackData(
 *   TrackPage,
 *   props => props.match.params.trackSlug,
 *   () => 'wlc'
 * );
 */
export const connectTrackData = (Component, getTrackSlug, getProductSlug, options = {}) => {
  const mapStateToProps = state => ({
    authToken: state.user.authToken,
    user: state.user
  });

  const ConnectedComponent = connect(mapStateToProps)(Component);

  return withTrackData(ConnectedComponent, getTrackSlug, getProductSlug, options);
};

export default useTrackData;
